import React, { useState, useEffect, useMemo, useRef } from 'react';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import ImageEditor from '@uppy/image-editor';
import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import '@uppy/image-editor/css/style.min.css';
import { apiService } from '../services/api';
import './TemplateModal.css';

const TemplateModal = ({ isOpen, onClose, onSave, template = null, isPromotional = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    type: 'TEXT',
    language: 'en',
    headerType: 'TEXT',
    header: '',
    headerMedia: '',
    body: '',
    footer: '',
    variables: [],
    buttons: []
  });
  const [errors, setErrors] = useState({});
  const [variableCount, setVariableCount] = useState(0);
  const [buttonCount, setButtonCount] = useState(0);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [isImageEdited, setIsImageEdited] = useState(false);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState('');
  const [uploadedDocumentUrl, setUploadedDocumentUrl] = useState('');
  const [uploadedDocumentName, setUploadedDocumentName] = useState('');
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [imageInfo, setImageInfo] = useState({ width: 0, height: 0, size: 0 });

  const uppyDashboardRef = useRef(null);
  const uppyVideoRef = useRef(null);
  const uppyDocumentRef = useRef(null);
  const bodyTextareaRef = useRef(null);

  const [bodyFormatted, setBodyFormatted] = useState('');
  const [activeFormats, setActiveFormats] = useState({});
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [uploadedMediaUrl, setUploadedMediaUrl] = useState('');
  const [variableSamples, setVariableSamples] = useState([]);

  // Safely extract a string from a variable that may be a raw DB object or a string
  const toVarString = (v) =>
    typeof v === 'string' ? v : (v?.name || v?.displayText || String(v?.example || ''));

  const isEditMode = !!template;

  const categories = [
    { value: 'UTILITY', label: 'Utility' },
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'AUTHENTICATION', label: 'Authentication' }
  ];

  const headerTypes = [
    { value: 'TEXT', label: 'Text' },
    { value: 'IMAGE', label: 'Image' },
    { value: 'VIDEO', label: 'Video' },
    { value: 'DOCUMENT', label: 'Document' }
  ];

  const buttonTypes = [
    { value: 'QUICK_REPLY', label: 'Quick Reply' },
    { value: 'URL', label: 'Visit Website' },
    { value: 'PHONE_NUMBER', label: 'Call Now' }
  ];

  // Initialize Uppy instance with ImageEditor
  const uppy = useMemo(() => {
    const uppyInstance = new Uppy({
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: ['image/*'],
        maxFileSize: 5 * 1024 * 1024 // 5MB
      },
      autoProceed: false
    });

    // Image Editor event handlers
    uppyInstance.on('file-editor:start', (file) => {
      console.log('Image editor opened for file:', file.name);
    });

    uppyInstance.on('file-editor:complete', (updatedFile) => {
      console.log('Image editing completed:', updatedFile);
      setIsImageEdited(true);
      
      const file = uppyInstance.getFile(updatedFile.id);
      if (file && file.data) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result;
          setUploadedImageUrl(dataUrl);
          setFormData(prev => ({ ...prev, headerMedia: dataUrl }));
        };
        reader.readAsDataURL(file.data);
      } else if (updatedFile.data) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result;
          setUploadedImageUrl(dataUrl);
          setFormData(prev => ({ ...prev, headerMedia: dataUrl }));
        };
        reader.readAsDataURL(updatedFile.data);
      }
    });

    uppyInstance.on('file-editor:cancel', (file) => {
      console.log('Image editing cancelled:', file.name);
    });

    uppyInstance.on('file-added', (file) => {
      console.log('File added:', file.name);
      if (file.data && file.source !== 'ImageEditor') {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result;
          setUploadedImageUrl(dataUrl);
          setFormData(prev => ({ ...prev, headerMedia: dataUrl }));
          setShowImageEditor(true);
          
          // Capture image info
          const img = new Image();
          img.onload = () => {
            setImageInfo({
              width: img.width,
              height: img.height,
              size: (file.size / 1024).toFixed(2) // KB
            });
          };
          img.src = dataUrl;
        };
        reader.readAsDataURL(file.data);
      }
    });

    uppyInstance.on('file-removed', () => {
      setUploadedImageUrl('');
      setFormData(prev => ({ ...prev, headerMedia: '' }));
      setIsImageEdited(false);
    });

    return uppyInstance;
  }, []);

  // Video Uppy instance
  const uppyVideo = useMemo(() => {
    const videoUppy = new Uppy({
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: ['video/*'],
        maxFileSize: 16 * 1024 * 1024 // 16MB for videos
      },
      autoProceed: false
    });

    videoUppy.on('file-added', (file) => {
      console.log('Video file added:', file.name);
      if (file.data) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result;
          setUploadedVideoUrl(dataUrl);
          setFormData(prev => ({ ...prev, headerMedia: dataUrl }));
        };
        reader.readAsDataURL(file.data);
      }
    });

    videoUppy.on('file-removed', () => {
      setUploadedVideoUrl('');
      setFormData(prev => ({ ...prev, headerMedia: '' }));
    });

    return videoUppy;
  }, []);

  // Document Uppy instance
  const uppyDocument = useMemo(() => {
    const docUppy = new Uppy({
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: ['application/pdf', '.pdf'],
        maxFileSize: 100 * 1024 * 1024 // 100MB for documents
      },
      autoProceed: false
    });

    docUppy.on('file-added', (file) => {
      console.log('Document file added:', file.name);
      setUploadedDocumentName(file.name);
      if (file.data) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result;
          setUploadedDocumentUrl(dataUrl);
          setFormData(prev => ({ ...prev, headerMedia: dataUrl }));
        };
        reader.readAsDataURL(file.data);
      }
    });

    docUppy.on('file-removed', () => {
      setUploadedDocumentUrl('');
      setUploadedDocumentName('');
      setFormData(prev => ({ ...prev, headerMedia: '' }));
    });

    return docUppy;
  }, []);

  // Initialize Dashboard and ImageEditor plugins
  useEffect(() => {
    if (!isOpen || !uppy || formData.headerType !== 'IMAGE') return;

    const timer = setTimeout(() => {
      if (uppyDashboardRef.current) {
        const existingDashboard = uppy.getPlugin('Dashboard');
        const existingImageEditor = uppy.getPlugin('ImageEditor');
        
        // Add ImageEditor plugin FIRST (must be added before Dashboard)
        if (!existingImageEditor) {
          uppy.use(ImageEditor, {
            id: 'ImageEditor',
            quality: 0.8,
            cropperOptions: {
              viewMode: 1,
              aspectRatio: NaN,
              autoCropArea: 0.8,
              responsive: true,
              restore: false,
              guides: true,
              center: true,
              highlight: false,
              cropBoxMovable: true,
              cropBoxResizable: true
            },
            actions: {
              revert: true,
              rotate: true,
              granularRotate: true,
              flip: true,
              zoomIn: true,
              zoomOut: true,
              cropSquare: true,
              cropWidescreen: true,
              cropWidescreenVertical: true
            }
          });
        }

        // Add Dashboard plugin AFTER ImageEditor
        if (!existingDashboard) {
          uppy.use(Dashboard, {
            target: uppyDashboardRef.current,
            inline: true,
            width: '100%',
            height: 450,
            showProgressDetails: true,
            proudlyDisplayPoweredByUppy: false,
            note: 'Images only, 1 file, up to 5 MB',
            autoOpenImageEditor: true
          });
        }
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (formData.headerType !== 'IMAGE') {
        const dashboard = uppy.getPlugin('Dashboard');
        const imageEditor = uppy.getPlugin('ImageEditor');
        if (dashboard) uppy.removePlugin(dashboard);
        if (imageEditor) uppy.removePlugin(imageEditor);
        uppy.cancelAll();
      }
    };
  }, [isOpen, formData.headerType, uppy]);

  // Initialize Video Upload
  useEffect(() => {
    if (!isOpen || !uppyVideo || formData.headerType !== 'VIDEO') return;

    const timer = setTimeout(() => {
      if (uppyVideoRef.current) {
        const existingDashboard = uppyVideo.getPlugin('Dashboard');
        
        if (!existingDashboard) {
          uppyVideo.use(Dashboard, {
            target: uppyVideoRef.current,
            inline: true,
            width: '100%',
            height: 450,
            showProgressDetails: true,
            proudlyDisplayPoweredByUppy: false,
            note: 'Videos only, 1 file, up to 16 MB'
          });
        }
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (formData.headerType !== 'VIDEO') {
        const dashboard = uppyVideo.getPlugin('Dashboard');
        if (dashboard) uppyVideo.removePlugin(dashboard);
        uppyVideo.cancelAll();
      }
    };
  }, [isOpen, formData.headerType, uppyVideo]);

  // Initialize Document Upload
  useEffect(() => {
    if (!isOpen || !uppyDocument || formData.headerType !== 'DOCUMENT') return;

    const timer = setTimeout(() => {
      if (uppyDocumentRef.current) {
        const existingDashboard = uppyDocument.getPlugin('Dashboard');
        
        if (!existingDashboard) {
          uppyDocument.use(Dashboard, {
            target: uppyDocumentRef.current,
            inline: true,
            width: '100%',
            height: 450,
            showProgressDetails: true,
            proudlyDisplayPoweredByUppy: false,
            note: 'PDF documents only, 1 file, up to 100 MB'
          });
        }
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (formData.headerType !== 'DOCUMENT') {
        const dashboard = uppyDocument.getPlugin('Dashboard');
        if (dashboard) uppyDocument.removePlugin(dashboard);
        uppyDocument.cancelAll();
      }
    };
  }, [isOpen, formData.headerType, uppyDocument]);

  const types = [
    { value: 'TEXT', label: 'Text' },
    { value: 'MEDIA', label: 'Media' },
    { value: 'INTERACTIVE', label: 'Interactive' }
  ];

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'hi', label: 'Hindi' }
  ];

  useEffect(() => {
    if (template) {
      // Extract data from template components structure (Meta API format)
      const bodyText = template.components?.body?.text || template.body || '';
      const headerComponent = template.components?.header;
      const headerText = headerComponent?.text || template.header || '';
      const headerType = headerComponent?.format || headerComponent?.type || 'TEXT';
      // Handle both 1D array ["handle"] and legacy 2D array [["handle"]]
      const headerHandleArr = headerComponent?.example?.header_handle;
      let headerMedia = '';
      if (headerHandleArr && headerHandleArr.length > 0) {
        headerMedia = Array.isArray(headerHandleArr[0]) 
          ? headerHandleArr[0][0] || ''  // 2D array: [["handle"]]
          : headerHandleArr[0] || '';     // 1D array: ["handle"]
      }
      const footerText = template.components?.footer?.text || template.footer || '';
      const buttons = template.components?.buttons || [];
      
      // Extract variable count — handle both named {{word}} and positional {{number}} formats
      const namedVarMatches = [...bodyText.matchAll(/\{\{([a-zA-Z_]\w*)\}\}/g)];
      const uniqueNamedVars = [...new Set(namedVarMatches.map(m => m[1]))];
      let extractedVarCount, loadedVarNames;
      if (uniqueNamedVars.length > 0) {
        extractedVarCount = uniqueNamedVars.length;
        loadedVarNames = uniqueNamedVars;
      } else {
        const bodyVars = bodyText.match(/\{\{(\d+)\}\}/g) || [];
        extractedVarCount = bodyVars.length > 0 ? Math.max(...bodyVars.map(v => parseInt(v.replace(/[{}]/g, '')))) : 0;
        loadedVarNames = template.variables
          ? template.variables.map(v => toVarString(v))
          : [];
      }
      const existingSamples = template.variables
        ? template.variables.map(v => (typeof v === 'object' && v !== null ? v.example || '' : ''))
        : [];

      setVariableCount(extractedVarCount);
      setButtonCount(buttons.length);
      setVariableSamples(Array.from({ length: extractedVarCount }, (_, i) => existingSamples[i] || ''));
      setFormData({
        name: template.displayName || template.name || '',
        category: template.category || (isPromotional ? 'MARKETING' : ''),
        type: template.type || 'TEXT',
        language: template.language || 'en',
        headerType: headerType,
        header: headerText,
        headerMedia: headerMedia,
        body: bodyText,
        footer: footerText,
        variables: loadedVarNames,
        buttons: buttons
      });
    } else {
      setFormData({
        name: '',
        category: isPromotional ? 'MARKETING' : '',
        type: 'TEXT',
        language: 'en',
        headerType: 'TEXT',
        header: '',
        headerMedia: '',
        body: '',
        footer: '',
        variables: [],
        buttons: []
      });
      setVariableCount(0);
      setButtonCount(0);
      setVariableSamples([]);
    }
    setErrors({});
  }, [template, isOpen, isPromotional]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Prepare the update object
    const updateObj = { [name]: value };
    
    // If headerType is changed, update type to MEDIA if it's an image/video/document
    if (name === 'headerType') {
      if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(value)) {
        updateObj.type = 'MEDIA';
      } else {
        updateObj.type = 'TEXT';
      }
    }
    
    setFormData(prev => ({
      ...prev,
      ...updateObj
    }));
    
    // Update variable count when body changes
    if (name === 'body') {
      const namedVarMatches = [...value.matchAll(/\{\{([a-zA-Z_]\w*)\}\}/g)];
      const uniqueNamedVars = [...new Set(namedVarMatches.map(m => m[1]))];

      if (uniqueNamedVars.length > 0) {
        // Named parameter format: {{invoice_no}}, {{amount}}, etc.
        const count = uniqueNamedVars.length;
        setVariableCount(count);
        setFormData(prev => ({ ...prev, variables: uniqueNamedVars }));
        setVariableSamples(prev => uniqueNamedVars.map((_, i) => prev[i] || ''));
      } else {
        // Positional parameter format: {{1}}, {{2}}, etc.
        const vars = value.match(/\{\{(\d+)\}\}/g) || [];
        const maxVar = vars.length > 0
          ? Math.max(...vars.map(v => parseInt(v.replace(/[{}]/g, ''))))
          : 0;
        setVariableCount(maxVar);
        // Auto-update variables array — preserve existing names, only add/trim
        setFormData(prev => {
          const existing = prev.variables || [];
          const newVariables = [];
          for (let i = 0; i < maxVar; i++) {
            newVariables.push(toVarString(existing[i]) || `variable_${i + 1}`);
          }
          return { ...prev, variables: newVariables };
        });
        // Sync variableSamples length
        setVariableSamples(prev => {
          const updated = [...prev];
          while (updated.length < maxVar) updated.push('');
          return updated.slice(0, maxVar);
        });
      }
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleVariableNameChange = (index, value) => {
    setFormData(prev => {
      const newVariables = [...prev.variables];
      newVariables[index] = value;
      return {
        ...prev,
        variables: newVariables
      };
    });
  };

  const handleVariableSampleChange = (index, value) => {
    setVariableSamples(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleAddButton = () => {
    if (buttonCount < 3) { // WhatsApp allows max 3 buttons
      setFormData(prev => ({
        ...prev,
        buttons: [...prev.buttons, { type: 'QUICK_REPLY', text: '', url: '', phone_number: '' }]
      }));
      setButtonCount(prev => prev + 1);
    }
  };

  const handleRemoveButton = (index) => {
    setFormData(prev => {
      const newButtons = prev.buttons.filter((_, i) => i !== index);
      return {
        ...prev,
        buttons: newButtons
      };
    });
    setButtonCount(prev => prev - 1);
  };

  const handleButtonChange = (index, field, value) => {
    setFormData(prev => {
      const newButtons = [...prev.buttons];
      newButtons[index] = {
        ...newButtons[index],
        [field]: value
      };
      return {
        ...prev,
        buttons: newButtons
      };
    });
  };

  const insertVariable = () => {
    const nextVar = variableCount + 1;
    const variable = `{{${nextVar}}}`;
    const textarea = document.getElementById('body');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newBody = formData.body.substring(0, start) + variable + formData.body.substring(end);
    
    setFormData(prev => ({
      ...prev,
      body: newBody,
      variables: prev.variables.length < nextVar 
        ? [...prev.variables, ...Array(nextVar - prev.variables.length).fill('')]
        : prev.variables
    }));
    
    setVariableCount(nextVar);
    
    // Focus back on textarea
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  // Text formatting functions
  const applyTextFormat = (formatType) => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.body.substring(start, end);

    if (!selectedText) {
      alert('Please select text to format');
      return;
    }

    let formattedText = selectedText;
    
    switch(formatType) {
      case 'bold':
        formattedText = `*${selectedText}*`;
        break;
      case 'italic':
        formattedText = `_${selectedText}_`;
        break;
      case 'code':
        formattedText = `` + selectedText + ``;
        break;
      case 'strikethrough':
        formattedText = `~${selectedText}~`;
        break;
      default:
        return;
    }

    const newBody = formData.body.substring(0, start) + formattedText + formData.body.substring(end);
    setFormData(prev => ({ ...prev, body: newBody }));

    // Reset selection
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  };

  const getFormatExample = (format) => {
    const examples = {
      bold: '*bold text*',
      italic: '_italic text_',
      code: '`code text`',
      strikethrough: '~strikethrough~'
    };
    return examples[format] || format;
  };

  const replaceVariablesWithNames = (text, variables) => {
    if (!text || !variables || variables.length === 0) return text;
    let result = text;
    variables.forEach((v, index) => {
      const varNumber = index + 1;
      const regex = new RegExp(`\\{\\{${varNumber}\\}\\}`, 'g');
      const displayName = toVarString(v) || `variable_${varNumber}`;
      result = result.replace(regex, `{{${displayName}}}`);
    });
    return result;
  };

  /**
   * Upload media file to server
   * Converts base64 data URL to File and uploads
   */
  const uploadMediaToServer = async (dataUrl, mediaType) => {
    try {
      setIsUploadingMedia(true);
      
      // Convert data URL to File
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      
      // Determine file extension based on mime type
      let fileExt = '.jpg'; // default
      if (blob.type === 'image/jpeg') fileExt = '.jpg';
      else if (blob.type === 'image/png') fileExt = '.png';
      else if (blob.type === 'image/gif') fileExt = '.gif';
      else if (blob.type === 'image/webp') fileExt = '.webp';
      else if (blob.type === 'video/mp4') fileExt = '.mp4';
      else if (blob.type === 'video/3gpp') fileExt = '.3gp';
      else if (blob.type === 'application/pdf') fileExt = '.pdf';
      
      const file = new File([blob], `media-${Date.now()}${fileExt}`, { type: blob.type });
      
      // Determine upload type based on headerType
      let uploadType = 'HEADER_IMAGE';
      if (mediaType === 'VIDEO') {
        uploadType = 'HEADER_VIDEO';
      } else if (mediaType === 'DOCUMENT') {
        uploadType = 'HEADER_DOCUMENT';
      }
      
      // Upload to server (backend performs Resumable Upload to Meta and returns the handle)
      const uploadResponse = await apiService.media.upload(file, uploadType);
      
      if (uploadResponse.data && uploadResponse.data.data) {
        const responseData = uploadResponse.data.data;

        // Prefer the Meta Resumable Upload handle (required for template header_handle).
        // Fall back to local URL only if the Meta upload was skipped/failed (e.g. no token configured).
        const mediaHandle = responseData.metaHandle
          || responseData.metaUpload?.metaHandle
          || null;

        if (!mediaHandle) {
          throw new Error(
            responseData.metaUpload?.error
              ? `Meta media upload failed: ${responseData.metaUpload.error}`
              : 'Meta media handle not returned. Ensure WABA access token is configured.'
          );
        }

        // Also extract the server-side media path for storing in database
        const mediaPath = responseData.mediaUrl 
          || responseData.mediaPath 
          || null;

        setUploadedMediaUrl(mediaHandle);
        setIsUploadingMedia(false);
        
        // Return both handle and path
        return {
          handle: mediaHandle,
          path: mediaPath,
          fileName: responseData.mediaFileName || responseData.filename,
          size: responseData.mediaSize,
          mimeType: responseData.mediaMimeType
        };
      }
      
      throw new Error('Failed to get media URL from server response');
    } catch (error) {
      console.error('Media upload error:', error);
      setIsUploadingMedia(false);
      throw error;
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.body.trim()) {
      newErrors.body = 'Body text is required';
    } else if (formData.body.length > 1024) {
      newErrors.body = 'Body text is too long (max 1024 characters)';
    }

    if (formData.header && formData.header.length > 60) {
      newErrors.header = 'Header is too long (max 60 characters)';
    }

    if (formData.footer && formData.footer.length > 60) {
      newErrors.footer = 'Footer is too long (max 60 characters)';
    }

    // Validate header based on type
    if (formData.headerType === 'TEXT' && formData.header && formData.header.length > 60) {
      newErrors.header = 'Header text is too long (max 60 characters)';
    }
    if ((formData.headerType === 'IMAGE' || formData.headerType === 'VIDEO' || formData.headerType === 'DOCUMENT') && !formData.headerMedia) {
      newErrors.headerMedia = 'Media URL or handle is required for Image/Video/Document header';
    }

    // Validate buttons
    if (formData.buttons.length > 3) {
      newErrors.buttons = 'Maximum 3 buttons allowed';
    }
    formData.buttons.forEach((button, index) => {
      if (!button.text || button.text.trim() === '') {
        newErrors[`button_${index}_text`] = 'Button text is required';
      }
      if (button.type === 'URL' && (!button.url || button.url.trim() === '')) {
        newErrors[`button_${index}_url`] = 'URL is required for Visit Website button';
      }
      if (button.type === 'PHONE_NUMBER' && (!button.phone_number || button.phone_number.trim() === '')) {
        newErrors[`button_${index}_phone`] = 'Phone number is required for Call Now button';
      }
    });

    // Validate variable format
    const hasNamedParams = /\{\{[a-zA-Z_]\w*\}\}/.test(formData.body);
    const bodyVars = formData.body.match(/\{\{(\d+)\}\}/g) || [];
    const varNumbers = bodyVars.map(v => parseInt(v.replace(/[{}]/g, '')));
    const maxVar = varNumbers.length > 0 ? Math.max(...varNumbers) : 0;
    
    if (!hasNamedParams && maxVar > 0) {
      // Positional variables must be sequential
      for (let i = 1; i <= maxVar; i++) {
        if (!formData.body.includes(`{{${i}}}`)) {
          newErrors.body = `Variable {{${i}}} is missing. Variables must be sequential starting from {{1}}`;
          break;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Format data according to Meta WhatsApp Business API structure
  const formatForAPI = (data) => {
    const components = [];

    // Detect named vs positional parameter format once, used throughout
    const namedParamMatches = [...data.body.matchAll(/\{\{([a-zA-Z_]\w*)\}\}/g)];
    const uniqueNamedParams = [...new Set(namedParamMatches.map(m => m[1]))];
    const isNamedParamFormat = uniqueNamedParams.length > 0;
    
    // Header component (if provided)
    if (data.headerType === 'TEXT' && data.header && data.header.trim()) {
      components.push({
        type: 'HEADER',
        format: 'TEXT',
        text: data.header
      });
    } else if ((data.headerType === 'IMAGE' || data.headerType === 'VIDEO' || data.headerType === 'DOCUMENT') && data.headerMedia) {
      const headerComponent = {
        type: 'HEADER',
        format: data.headerType,
        example: {
          header_handle: [data.headerMedia]  // Meta expects 1D array of strings
        }
      };
      
      // Also include server-side media path for database storage
      if (data.mediaPath) {
        headerComponent.mediaPath = data.mediaPath;
        headerComponent.mediaFileName = data.mediaFileName;
        headerComponent.mediaSize = data.mediaSize;
        headerComponent.mediaMimeType = data.mediaMimeType;
        
        console.log('[formatForAPI] Added media fields to header:', {
          mediaPath: data.mediaPath,
          mediaFileName: data.mediaFileName,
          mediaSize: data.mediaSize,
          mediaMimeType: data.mediaMimeType
        });
      } else {
        console.log('[formatForAPI] WARNING: Media upload occurred but mediaPath not set in formData', {
          headerType: data.headerType,
          has_headerMedia: !!data.headerMedia,
          data_keys: Object.keys(data)
        });
      }
      
      components.push(headerComponent);
    }
    
    // Body component (required)
    let bodyExampleData;
    if (isNamedParamFormat) {
      // Named parameter format: body_text_named_params
      bodyExampleData = {
        body_text_named_params: uniqueNamedParams.map((paramName, idx) => ({
          param_name: paramName,
          example: data.variableSamples?.[idx]?.trim() || `sample_${paramName}`
        }))
      };
    } else {
      // Positional parameter format: body_text
      const bodyVars = data.body.match(/\{\{(\d+)\}\}/g) || [];
      const exampleValues = [];
      bodyVars.forEach((_, index) => {
        const userSample = data.variableSamples?.[index];
        if (userSample && userSample.trim()) {
          exampleValues.push(userSample.trim());
        } else {
          const varName = toVarString(data.variables[index]) || `variable_${index + 1}`;
          if (varName.toLowerCase().includes('name')) {
            exampleValues.push('John');
          } else if (varName.toLowerCase().includes('order') || varName.toLowerCase().includes('number')) {
            exampleValues.push('ORD-12345');
          } else if (varName.toLowerCase().includes('amount') || varName.toLowerCase().includes('price')) {
            exampleValues.push('$99.99');
          } else if (varName.toLowerCase().includes('date')) {
            exampleValues.push('Jan 25, 2024');
          } else if (varName.toLowerCase().includes('link') || varName.toLowerCase().includes('url')) {
            exampleValues.push('https://example.com');
          } else {
            exampleValues.push(`Value ${index + 1}`);
          }
        }
      });
      bodyExampleData = { body_text: exampleValues.length > 0 ? [exampleValues] : [[]] };
    }

    // Push body component
    components.push({
      type: 'BODY',
      text: data.body,
      example: bodyExampleData
    });
    
    // Footer component (if provided)
    if (data.footer && data.footer.trim()) {
      components.push({
        type: 'FOOTER',
        text: data.footer
      });
    }

    // Buttons component (if provided)
    if (data.buttons && data.buttons.length > 0) {
      const formattedButtons = data.buttons.map(btn => {
        const button = {
          type: btn.type,
          text: btn.text
        };
        if (btn.type === 'URL') {
          button.url = btn.url;
        } else if (btn.type === 'PHONE_NUMBER') {
          button.phone_number = btn.phone_number;
        }
        return button;
      });
      components.push({
        type: 'BUTTONS',
        buttons: formattedButtons
      });
    }
    
    // Meta WhatsApp Business API format
    return {
      name: data.name.toLowerCase().replace(/\s+/g, '_'), // Convert to lowercase with underscores
      language: data.language,
      category: data.category.toUpperCase(), // UTILITY, MARKETING, or AUTHENTICATION
      ...(isNamedParamFormat && { parameter_format: 'NAMED' }),
      components: components,
      // Additional metadata for our app
      displayName: data.name,
      // Send variables as objects so the backend can store the user-entered names and examples
      variables: data.variables.map((v, idx) => {
        const sample = data.variableSamples?.[idx]?.trim();
        return typeof v === 'string'
          ? { name: v, type: 'text', displayText: v, example: sample || `example_${idx + 1}`, required: true }
          : { ...v, example: sample || v.example };
      }),
      type: data.type,
      headerType: data.headerType,
      mediaPath: data.mediaPath,       // Server-side media path for media headers
      mediaFileName: data.mediaFileName,
      mediaSize: data.mediaSize,
      mediaMimeType: data.mediaMimeType,
      buttons: data.buttons
    };
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      // If header media is present, upload it first
      let updatedFormData = { ...formData, variableSamples };
      let mediaInfo = null;
      
      if (updatedFormData.headerMedia && updatedFormData.headerType !== 'TEXT') {
        // Check if it's a data URL (not already uploaded)
        if (updatedFormData.headerMedia.startsWith('data:')) {
          mediaInfo = await uploadMediaToServer(updatedFormData.headerMedia, updatedFormData.headerType);
          updatedFormData.headerMedia = mediaInfo.handle; // Store Meta handle for API
          updatedFormData.mediaPath = mediaInfo.path;     // Store server path for database
          updatedFormData.mediaFileName = mediaInfo.fileName;
          updatedFormData.mediaSize = mediaInfo.size;
          updatedFormData.mediaMimeType = mediaInfo.mimeType;
          
          // Debug logging
          console.log('[TemplateModal] Media upload response:', {
            handle: mediaInfo.handle,
            path: mediaInfo.path,
            fileName: mediaInfo.fileName,
            size: mediaInfo.size,
            mimeType: mediaInfo.mimeType
          });
        }
      }
      
      // Format data according to Meta WhatsApp Business API structure
      const apiFormattedData = formatForAPI(updatedFormData);
      
      // Debug: Check if mediaPath made it into formatted data
      console.log('[TemplateModal] Formatted API data for backend:', {
        mediaPath: apiFormattedData.mediaPath,
        components: apiFormattedData.components
      });
      
      // Call parent save handler (dispatches Redux action)
      await onSave(apiFormattedData);
      
      // onClose is called by parent after successful save
    } catch (error) {
      console.error('Error saving template:', error);
      setErrors({ submit: error.message || 'Failed to save template. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content template-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Template' : 'Create New Template'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="template-modal-body">
          <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Template Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Order Confirmation"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={errors.category ? 'error form-select' : 'form-select'}
              >
                <option value="">Select category</option>
                {categories.map(category => (
                  <option key={category.value} value={category.value}>{category.label}</option>
                ))}
              </select>
              <div className="category-guidelines">
                <small className="field-hint">
                  <strong>UTILITY:</strong> Transactional messages (Order confirmation, delivery updates, invoices)
                </small>
                <small className="field-hint">
                  <strong>MARKETING:</strong> Promotional content (Offers, campaigns, announcements)
                </small>
                <small className="field-hint">
                  <strong>AUTHENTICATION:</strong> OTP/verification codes (1-minute expiry codes only)
                </small>
              </div>
              {errors.category && <span className="error-message">{errors.category}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Template Type</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="form-select"
              >
                {types.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="language">Language</label>
              <select
                id="language"
                name="language"
                value={formData.language}
                onChange={handleChange}
                className="form-select"
              >
                {languages.map(lang => (
                  <option key={lang.value} value={lang.value}>{lang.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="headerType">Header Type (Optional)</label>
            <select
              id="headerType"
              name="headerType"
              value={formData.headerType}
              onChange={handleChange}
              className="form-select"
            >
              {headerTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            {formData.headerType === 'TEXT' && (
              <>
                <input
                  type="text"
                  id="header"
                  name="header"
                  value={formData.header}
                  onChange={handleChange}
                  placeholder="Template header text"
                  maxLength={60}
                  className={errors.header ? 'error' : ''}
                  style={{ marginTop: '8px' }}
                />
                <div className="char-count">{formData.header.length} / 60</div>
                {errors.header && <span className="error-message">{errors.header}</span>}
              </>
            )}
            {formData.headerType === 'IMAGE' && (
              <>
                <div className="uppy-upload-container" style={{ marginTop: '8px' }}>
                  <div ref={uppyDashboardRef}></div>
                </div>
                {uploadedImageUrl && (
                  <div className={`image-preview-wrapper ${isImageEdited ? 'has-edited' : ''}`}>
                    <div className="image-preview-container">
                      <div className="image-preview">
                        <img src={uploadedImageUrl} alt="Header preview" />
                      </div>
                      <div className="image-actions">
                        <button
                          type="button"
                          className="edit-image-btn"
                          onClick={() => setShowImageEditor(!showImageEditor)}
                          title="Edit image with crop, rotate, flip, zoom tools"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => {
                            uppy.cancelAll();
                            setUploadedImageUrl('');
                            setFormData(prev => ({ ...prev, headerMedia: '' }));
                            setIsImageEdited(false);
                            setShowImageEditor(false);
                            setImageInfo({ width: 0, height: 0, size: 0 });
                          }}
                          title="Remove image"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                          Remove
                        </button>
                      </div>
                    </div>
                    {imageInfo.width > 0 && (
                      <div className="image-info-card">
                        <div className="info-item">
                          <span className="info-label">Dimensions:</span>
                          <span className="info-value">{imageInfo.width} × {imageInfo.height} px</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">File Size:</span>
                          <span className="info-value">{imageInfo.size} KB</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">Status:</span>
                          <span className="info-value">{isImageEdited ? '✓ Edited' : 'Original'}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {showImageEditor && uploadedImageUrl && (
                  <div className="image-editor-area">
                    <div className="editor-header">
                      <h4>Image Editor</h4>
                      <button
                        type="button"
                        className="close-editor-btn"
                        onClick={() => setShowImageEditor(false)}
                        title="Close editor"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="editor-tips">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                      </svg>
                      <span>Use the toolbar below to crop, rotate, flip, and zoom your image</span>
                    </div>
                    <div className="editor-tools-note">
                      <p><strong>Available Tools:</strong></p>
                      <ul>
                        <li>✓ Crop - Adjust image area and aspect ratio</li>
                        <li>✓ Rotate - Rotate image by any angle</li>
                        <li>✓ Flip - Flip horizontally or vertically</li>
                        <li>✓ Zoom - Zoom in and out</li>
                        <li>✓ Aspect Ratio - Square, widescreen, or custom</li>
                      </ul>
                    </div>
                  </div>
                )}
              </>
            )}
            {formData.headerType === 'VIDEO' && (
              <>
                <div className="uppy-upload-container" style={{ marginTop: '8px' }}>
                  <div ref={uppyVideoRef}></div>
                </div>
                {uploadedVideoUrl && (
                  <div className="video-preview">
                    <video 
                      src={uploadedVideoUrl} 
                      controls
                      style={{ width: '100%', height: 'auto', maxHeight: '200px', borderRadius: '8px' }}
                    />
                    <button
                      type="button"
                      className="remove-media-btn"
                      onClick={() => {
                        uppyVideo.cancelAll();
                        setUploadedVideoUrl('');
                        setFormData(prev => ({ ...prev, headerMedia: '' }));
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}
                <div className="media-url-fallback" style={{ marginTop: '12px' }}>
                  <label>Or enter video URL / Media Handle</label>
                  <input
                    type="text"
                    id="headerMedia"
                    name="headerMedia"
                    value={formData.headerMedia}
                    onChange={handleChange}
                    placeholder="Enter video URL or Meta media handle"
                    className={errors.headerMedia ? 'error' : ''}
                  />
                </div>
                {errors.headerMedia && <span className="error-message">{errors.headerMedia}</span>}
                <small className="field-hint">
                  Upload a video or provide the media URL/handle from Meta's media library (Max 16 MB)
                </small>
              </>
            )}
            {formData.headerType === 'DOCUMENT' && (
              <>
                <div className="uppy-upload-container" style={{ marginTop: '8px' }}>
                  <div ref={uppyDocumentRef}></div>
                </div>
                {uploadedDocumentUrl && (
                  <div className="document-preview">
                    <div className="document-preview-item">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                      <div className="document-info">
                        <div className="document-name">{uploadedDocumentName || 'Document.pdf'}</div>
                        <div className="document-size">PDF Document</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="remove-media-btn"
                      onClick={() => {
                        uppyDocument.cancelAll();
                        setUploadedDocumentUrl('');
                        setUploadedDocumentName('');
                        setFormData(prev => ({ ...prev, headerMedia: '' }));
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}
                <div className="media-url-fallback" style={{ marginTop: '12px' }}>
                  <label>Or enter document URL / Media Handle</label>
                  <input
                    type="text"
                    id="headerMedia"
                    name="headerMedia"
                    value={formData.headerMedia}
                    onChange={handleChange}
                    placeholder="Enter document URL or Meta media handle"
                    className={errors.headerMedia ? 'error' : ''}
                  />
                </div>
                {errors.headerMedia && <span className="error-message">{errors.headerMedia}</span>}
                <small className="field-hint">
                  Upload a PDF document or provide the media URL/handle from Meta's media library (Max 100 MB)
                </small>
              </>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="body">Body Text *</label>
            <div className="template-tips">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <span className="tips-text">Max 1024 characters • Use variables for dynamic content • Variables must be sequential</span>
            </div>
            <div className="body-editor">
              <div className="editor-toolbar">
                <div className="toolbar-group">
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => applyTextFormat('bold')}
                    title="Bold text - Wrap with *asterisks*"
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => applyTextFormat('italic')}
                    title="Italic text - Wrap with _underscores_"
                  >
                    <em>I</em>
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => applyTextFormat('code')}
                    title="Code text - Wrap with backticks"
                  >
                    {'<'}code{'>'}
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn"
                    onClick={() => applyTextFormat('strikethrough')}
                    title="Strikethrough - Wrap with ~tildes~"
                  >
                    <s>S</s>
                  </button>
                </div>
                <div className="toolbar-separator"></div>
                <button
                  type="button"
                  className="toolbar-btn"
                  onClick={insertVariable}
                  title="Insert Variable"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  Insert Variable
                </button>
                <span className="variable-hint">Use {'{{'}1{'}}'}, {'{{'}2{'}}'}, etc. for variables</span>
              </div>
              <div className="format-guide">
                <details>
                  <summary>📝 Text Formatting Guide</summary>
                  <div className="guide-content">
                    <div className="guide-item">
                      <span className="guide-syntax">*text*</span>
                      <span className="guide-result">→ <strong>text</strong></span>
                    </div>
                    <div className="guide-item">
                      <span className="guide-syntax">_text_</span>
                      <span className="guide-result">→ <em>text</em></span>
                    </div>
                    <div className="guide-item">
                      <span className="guide-syntax">`text`</span>
                      <span className="guide-result">→ monospace text</span>
                    </div>
                    <div className="guide-item">
                      <span className="guide-syntax">~text~</span>
                      <span className="guide-result">→ <s>text</s></span>
                    </div>
                  </div>
                </details>
              </div>
              <textarea
                ref={bodyTextareaRef}
                id="body"
                name="body"
                value={formData.body}
                onChange={handleChange}
                placeholder="Enter template body text. Use {{1}}, {{2}} for positional variables or {{param_name}} for named variables..."
                rows="6"
                className={errors.body ? 'error' : ''}
                maxLength={1024}
              />
              <div className="char-count">{formData.body.length} / 1024</div>
            </div>
            {errors.body && <span className="error-message">{errors.body}</span>}
            
            {formData.body && variableCount > 0 && (
              <div className="body-preview">
                <strong>Preview with variable names:</strong>
                <div className="preview-content">
                  {replaceVariablesWithNames(formData.body, formData.variables)}
                </div>
              </div>
            )}
          </div>

          {variableCount > 0 && (
            <div className="form-group">
              <label>Variable Names &amp; Samples</label>
              <p className="variable-help-text">
                {/\{\{[a-zA-Z_]\w*\}\}/.test(formData.body)
                  ? 'Named variables detected from your body text. Enter a sample value for each to help Meta review your template.'
                  : 'Assign a descriptive name and a sample value for each variable to help Meta review your template.'}
              </p>
              <div className="variables-table">
                <div className="variables-table-header">
                  <span>Variable</span>
                  {!/\{\{[a-zA-Z_]\w*\}\}/.test(formData.body) && <span>Name</span>}
                  <span>Sample value</span>
                </div>
                {/\{\{[a-zA-Z_]\w*\}\}/.test(formData.body)
                  ? formData.variables.map((varName, idx) => (
                      <div key={idx} className="variables-table-row">
                        <code className="variable-code">{`{{${varName}}}`}</code>
                        <input
                          type="text"
                          value={variableSamples[idx] || ''}
                          onChange={(e) => handleVariableSampleChange(idx, e.target.value)}
                          placeholder={`e.g., sample_${varName}`}
                          className="variable-input"
                        />
                      </div>
                    ))
                  : Array.from({ length: variableCount }, (_, i) => i).map(idx => (
                      <div key={idx} className="variables-table-row">
                        <span className="variable-label">{`{{${idx + 1}}}`}</span>
                        <input
                          type="text"
                          value={toVarString(formData.variables[idx]) || ''}
                          onChange={(e) => handleVariableNameChange(idx, e.target.value)}
                          placeholder={`e.g., customer_name`}
                          className="variable-input"
                          required
                        />
                        <input
                          type="text"
                          value={variableSamples[idx] || ''}
                          onChange={(e) => handleVariableSampleChange(idx, e.target.value)}
                          placeholder={`e.g., John`}
                          className="variable-input"
                        />
                      </div>
                    ))}
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="footer">Footer (Optional, max 60 chars)</label>
            <input
              type="text"
              id="footer"
              name="footer"
              value={formData.footer}
              onChange={handleChange}
              placeholder="Template footer text"
              maxLength={60}
              className={errors.footer ? 'error' : ''}
            />
            <div className="char-count">{formData.footer.length} / 60</div>
            {errors.footer && <span className="error-message">{errors.footer}</span>}
          </div>

          <div className="section-divider">
            <h3>Action Buttons (Optional, max 3)</h3>
            <small className="field-hint">Add call-to-action buttons to increase engagement</small>
            <div className="button-types-guide">
              <span className="guide-item">💬 Quick Reply: Customer responds with predefined text</span>
              <span className="guide-item">🔗 Visit Website: Direct link to your website</span>
              <span className="guide-item">📞 Call Now: Phone number for direct calls</span>
            </div>
          </div>

          {formData.buttons.map((button, index) => (
            <div key={index} className="button-editor">
              <div className="button-header">
                <label>Button {index + 1}</label>
                {formData.buttons.length > 0 && (
                  <button
                    type="button"
                    className="btn-remove-button"
                    onClick={() => handleRemoveButton(index)}
                    title="Remove Button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                )}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Button Type</label>
                  <select
                    value={button.type}
                    onChange={(e) => handleButtonChange(index, 'type', e.target.value)}
                    className="form-select"
                  >
                    {buttonTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Button Text *</label>
                  <input
                    type="text"
                    value={button.text}
                    onChange={(e) => handleButtonChange(index, 'text', e.target.value)}
                    placeholder="Button label"
                    maxLength={20}
                    className={errors[`button_${index}_text`] ? 'error' : ''}
                  />
                  {errors[`button_${index}_text`] && (
                    <span className="error-message">{errors[`button_${index}_text`]}</span>
                  )}
                </div>
              </div>
              {button.type === 'URL' && (
                <div className="form-group">
                  <label>Website URL *</label>
                  <input
                    type="url"
                    value={button.url}
                    onChange={(e) => handleButtonChange(index, 'url', e.target.value)}
                    placeholder="https://example.com"
                    className={errors[`button_${index}_url`] ? 'error' : ''}
                  />
                  {errors[`button_${index}_url`] && (
                    <span className="error-message">{errors[`button_${index}_url`]}</span>
                  )}
                </div>
              )}
              {button.type === 'PHONE_NUMBER' && (
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    value={button.phone_number}
                    onChange={(e) => handleButtonChange(index, 'phone_number', e.target.value)}
                    placeholder="+1234567890"
                    className={errors[`button_${index}_phone`] ? 'error' : ''}
                  />
                  {errors[`button_${index}_phone`] && (
                    <span className="error-message">{errors[`button_${index}_phone`]}</span>
                  )}
                </div>
              )}
            </div>
          ))}

          {formData.buttons.length < 3 && (
            <button
              type="button"
              className="btn-add-button"
              onClick={handleAddButton}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Button
            </button>
          )}
          {errors.buttons && <span className="error-message">{errors.buttons}</span>}

          <div className="template-checklist">
            <div className="checklist-title">Before you submit:</div>
            <div className="checklist-items">
              <div className="checklist-item">
                <input type="checkbox" id="check1" disabled defaultChecked={!!formData.name} />
                <label htmlFor="check1">Template name is unique and descriptive</label>
              </div>
              <div className="checklist-item">
                <input type="checkbox" id="check2" disabled defaultChecked={!!formData.category} />
                <label htmlFor="check2">Category matches your message type</label>
              </div>
              <div className="checklist-item">
                <input type="checkbox" id="check3" disabled defaultChecked={formData.body.length > 0} />
                <label htmlFor="check3">Body text is clear and compliant</label>
              </div>
              <div className="checklist-item">
                <input type="checkbox" id="check4" disabled defaultChecked={variableCount === 0 || formData.variables.length === variableCount} />
                <label htmlFor="check4">All variables have descriptive names</label>
              </div>
            </div>
          </div>

          {errors.submit && (
            <div className="submit-error-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{errors.submit}</span>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={isUploadingMedia || isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={isUploadingMedia || isSubmitting}>
              {isSubmitting 
                ? 'Submitting...' 
                : isUploadingMedia 
                  ? 'Uploading media...' 
                  : (isEditMode ? 'Update Template' : 'Create Template')}
            </button>
          </div>
          </form>

          <div className="whatsapp-preview-sidebar">
            <div className="preview-header">
              <h3>WhatsApp Preview</h3>
              <small className="field-hint">See how your message will appear</small>
            </div>
            <div className="whatsapp-preview-container">
              <div className="whatsapp-preview">
                <div className="whatsapp-header-bar">
                  <div className="whatsapp-contact">
                    <div className="whatsapp-avatar">B</div>
                    <div className="whatsapp-contact-info">
                      <div className="whatsapp-contact-name">Business Name</div>
                      <div className="whatsapp-contact-status">Business Account</div>
                    </div>
                  </div>
                </div>

                <div className="whatsapp-message-bubble">
                  {formData.headerType === 'TEXT' && formData.header && (
                    <div className="whatsapp-message-header">
                      <strong>{formData.header}</strong>
                    </div>
                  )}
                {formData.headerType === 'IMAGE' && (uploadedImageUrl || formData.headerMedia) && (
                  <div className="whatsapp-message-header-media">
                    {uploadedImageUrl ? (
                      <img 
                        src={uploadedImageUrl} 
                        alt="Header" 
                        style={{ width: '100%', height: 'auto', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px' }}
                      />
                    ) : (
                      <div className="media-placeholder image-placeholder">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                        <span>Image</span>
                      </div>
                    )}
                  </div>
                )}
                {formData.headerType === 'VIDEO' && formData.headerMedia && (
                  <div className="whatsapp-message-header-media">
                    {uploadedVideoUrl ? (
                      <video 
                        src={uploadedVideoUrl}
                        style={{ width: '100%', height: 'auto', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px' }}
                        controls
                      />
                    ) : (
                      <div className="media-placeholder video-placeholder">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="5 3 19 12 5 21 5 3"></polygon>
                        </svg>
                        <span>Video</span>
                      </div>
                    )}
                  </div>
                )}
                {formData.headerType === 'DOCUMENT' && formData.headerMedia && (
                  <div className="whatsapp-message-header-media">
                    {uploadedDocumentUrl ? (
                      <div className="document-preview-in-message">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        <span>{uploadedDocumentName || 'Document.pdf'}</span>
                      </div>
                    ) : (
                      <div className="media-placeholder document-placeholder">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        <span>Document</span>
                      </div>
                    )}
                  </div>
                )}

                  <div className="whatsapp-message-body">
                    {formData.body ? (() => {
                      let previewText = formData.body;
                      // Replace all variables with example values
                      const varMatches = formData.body.match(/\{\{(\d+)\}\}/g) || [];
                      varMatches.forEach((varMatch) => {
                        const varNumber = parseInt(varMatch.replace(/[{}]/g, ''));
                        const varIndex = varNumber - 1;
          const varName = toVarString(formData.variables[varIndex]) || `variable_${varNumber}`;
                        
                        // Generate example value based on variable name
                        let exampleValue = 'Example';
                        if (varName.toLowerCase().includes('name')) {
                          exampleValue = 'John';
                        } else if (varName.toLowerCase().includes('order') || varName.toLowerCase().includes('number')) {
                          exampleValue = 'ORD-12345';
                        } else if (varName.toLowerCase().includes('amount') || varName.toLowerCase().includes('price')) {
                          exampleValue = '$99.99';
                        } else if (varName.toLowerCase().includes('date')) {
                          exampleValue = 'Jan 25, 2024';
                        } else if (varName.toLowerCase().includes('link') || varName.toLowerCase().includes('url')) {
                          exampleValue = 'https://example.com';
                        } else if (varName.toLowerCase().includes('discount') || varName.toLowerCase().includes('percentage')) {
                          exampleValue = '50%';
                        } else if (varName.toLowerCase().includes('code') || varName.toLowerCase().includes('promo')) {
                          exampleValue = 'PROMO123';
                        } else {
                          exampleValue = `Value ${varNumber}`;
                        }
                        
                        previewText = previewText.replace(varMatch, `{{${exampleValue}}}`);
                      });
                      
                      // Split by variable markers and render
                      return (
                        <p>
                          {previewText.split(/(\{\{[^}]+\}\})/).map((part, index) => {
                            const varMatch = part.match(/\{\{([^}]+)\}\}/);
                            if (varMatch) {
                              return (
                                <span key={index} className="variable-preview-value">
                                  {varMatch[1]}
                                </span>
                              );
                            }
                            return <span key={index}>{part}</span>;
                          })}
                        </p>
                      );
                    })() : (
                      <p className="placeholder-text">Your message body will appear here...</p>
                    )}
                  </div>

                  {formData.footer && (
                    <div className="whatsapp-message-footer">
                      {formData.footer}
                    </div>
                  )}

                  {formData.buttons && formData.buttons.length > 0 && (
                    <div className="whatsapp-message-buttons">
                      {formData.buttons.map((button, index) => (
                        <button
                          key={index}
                          type="button"
                          className={`whatsapp-button whatsapp-button-${button.type.toLowerCase().replace('_', '-')}`}
                          disabled
                        >
                          {button.type === 'QUICK_REPLY' && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                            </svg>
                          )}
                          {button.type === 'URL' && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                              <polyline points="15 3 21 3 21 9"></polyline>
                              <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                          )}
                          {button.type === 'PHONE_NUMBER' && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                            </svg>
                          )}
                          <span>{button.text || `Button ${index + 1}`}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;

